import React, { createContext, useContext, useCallback, useReducer, useRef, useEffect, ReactNode } from "react";
import { ILoadPaginatedForOrg } from "@smartrr/shared/utils/paginatedQuery";
import { getCustomers } from "@vendor-app/app/_state/actionCreators/customers";
import {
  CustomerListState,
  CustomerListAction,
  customerListReducer,
  initialState,
  customerListActions,
} from "./customerListReducer";

interface CustomerListContextValue {
  state: CustomerListState;
  dispatch: React.Dispatch<CustomerListAction>;
}

const CustomerListContext = createContext<CustomerListContextValue | undefined>(undefined);

export function CustomerListProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(customerListReducer, initialState);
  const stateRef = useRef(state);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const fetchCustomers = useCallback(
    async (overrides?: { searchQuery?: string; pageNumber?: number; pageSize?: number }) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const currentState = stateRef.current;
      const searchQuery = overrides?.searchQuery ?? currentState.searchQuery;
      const pageNumber = overrides?.pageNumber ?? currentState.pageNumber;
      const pageSize = overrides?.pageSize ?? currentState.pageSize;

      dispatch(customerListActions.fetchCustomersStart());

      const queryParams: ILoadPaginatedForOrg["queryParams"] = {
        pageNumber,
        pageSize,
        orderBy: {
          customerShopifyId: "ASC",
        },
      };

      if (searchQuery.trim()) {
        queryParams.filterLike = {
          emailOrName: searchQuery.trim(),
        };
      }

      try {
        const response = await getCustomers({ queryParams, signal: abortController.signal });

        if (abortController.signal.aborted) {
          return;
        }

        if (response.type === "success") {
          dispatch(
            customerListActions.fetchCustomersSuccess({
              customers: response.body.data,
              totalCount: response.body.totalCount,
              totalPages: response.body.totalPages,
              pageSize: response.body.pageSize,
              pageNumber: response.body.pageNumber,
            })
          );
        } else {
          dispatch(customerListActions.fetchCustomersError());
        }
      } catch {
        if (abortController.signal.aborted) {
          return;
        }
        dispatch(customerListActions.fetchCustomersError());
      }
    },
    []
  );

  useEffect(() => {
    void fetchCustomers();
  }, [state.searchQuery, state.pageNumber, fetchCustomers]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const value: CustomerListContextValue = {
    state,
    dispatch,
  };

  return <CustomerListContext.Provider value={value}>{children}</CustomerListContext.Provider>;
}

export function useCustomerList(): CustomerListContextValue {
  const context = useContext(CustomerListContext);
  if (context === undefined) {
    throw new Error("useCustomerList must be used within a CustomerListProvider");
  }
  return context;
}
